import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { BackArrow, Button, CheckBox, Input, TopProgress, Typography } from '../../../../components'
import { Container } from '../styles'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { clientService } from '../../../../api'
import { useUi } from '../../../../context/ui.context'
import { usePageTitle } from '../../../../context/navigationContext'

const CreateUser:FC = () => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const {viewAlert} = useUi()
  usePageTitle(t("PAGE_TITLE_USER_CREATE"))
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue
  } = useFormik({
    validationSchema: yup.object().shape({
      first_name: yup.string().required(`${t('REQUIRED_FIELD')}`),
      last_name: yup.string().required(`${t('REQUIRED_FIELD')}`),
      email: yup.string().email(`${t('INVALID_EMAIL')}`).required(`${t('REQUIRED_FIELD')}`),
      external_auth_id: yup.string(),
      password: yup.string().min(6, `${t('MIN_VALUE')} 6`).required(`${t('REQUIRED_FIELD')}`),
      is_staff: yup.boolean(),
      is_active: yup.boolean(),
      email_verified: yup.boolean(),
      is_superuser: yup.boolean(),
    }),
    initialValues: {
      first_name:'',
      last_name:'',
      email:'',
      external_auth_id:'',
      password:'',
      is_staff:false,
      is_active:true,
      email_verified:false,
      is_superuser:false,
    },
    onSubmit: (vals:any) => {
      setLoading(true)
      clientService.createUser({
        ...vals,
        username: vals.email,
      })
      .then(() => {
        setLoadingComplete(true)
        setTimeout(() => {
          setLoading(false)
          setLoadingComplete(false)
          navigate('/clients-users/users')
        }, 800)
      })
      .catch((e:any) => {
        if(e?.response?.data){
          viewAlert({
            severity:'error',
            message: e.response?.data?.detail || t('ERROR_OCCURRED')
          })
        }
        setLoading(false)
      })
    }
  })

  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  }

  return (
    <Container>
      <BackArrow fallbackPath="/clients-users/users"/>
      <Typography variant='h1'>{t("CREATE_NEW_USER")}</Typography>
      <div style={{display:'flex', flexDirection:'column', gap:'16px', maxWidth:'520px', marginTop:'16px'}}>
        <Input
          required
          fieldName='first_name'
          title={t("FIRST_NAME")}
          placeholder=''
          {...formikProps}
        />
        <Input
          required
          fieldName='last_name'
          title={t("LAST_NAME")}
          placeholder=''
          {...formikProps}
        />
        <Input
          required
          fieldName='email'
          title={t("EMAIL")}
          placeholder=''
          {...formikProps}
        />
        <Input
          fieldName='external_auth_id'
          title={t("EXTERNAL_AUTH_ID")}
          placeholder=''
          {...formikProps}
        />
        <Input
          required
          fieldName='password'
          title={t("PASSWORD")}
          type='password'
          placeholder=''
          {...formikProps}
        />
        <CheckBox
          title={t("STAFF_USER")}
          value={values.is_staff}
          onChange={() => setFieldValue('is_staff', !values.is_staff)}
        />
        <CheckBox
          title={t("IS_ACTIVE")}
          value={values.is_active}
          onChange={() => setFieldValue('is_active', !values.is_active)}
        />
        <CheckBox
          title={t("EMAIL_VERIFIED")}
          value={values.email_verified}
          onChange={() => setFieldValue('email_verified', !values.email_verified)}
        />
        <CheckBox
          title={t("IS_SUPERUSER")}
          value={values.is_superuser}
          onChange={() => setFieldValue('is_superuser', !values.is_superuser)}
        />
        <div style={{display:'flex', gap:'10px', marginTop:'8px'}}>
          <Button
            variant="primary"
            size="display"
            onClick={handleSubmit}
            loading={loading}
            loadingComplete={loadingComplete}
            title={t("CREATE")}
          />
          <Button
            variant="stroke"
            size="display"
            onClick={() => navigate('/clients-users/users')}
            title={t("CANCEL")}
          />
        </div>
      </div>
      <TopProgress loading={loading}/>
    </Container>
  )
}

export default CreateUser
